// /**
//  * Appointment Detail Component
//  * - Displays comprehensive appointment information
//  * - Shows pet, owner, veterinarian, and scheduling details
//  * - Includes appointment status and notes
//  * - Responsive two-column layout for details
//  */

// import React from "react";
// import { FaRegCalendarAlt, FaRegClock, FaRegUser, FaPaw, FaStethoscope } from "react-icons/fa";
// import AvatarBlock from "./Shared/AvatarBlock";
// import InfoRow from "./Shared/InfoRow";
// import StatusBadge from "./Shared/StatusBadge";
// import SectionHeader from "./Shared/SectionHeader";
// import { formatDateTime } from "./Shared/helpers";
// import { PiNotepadBold } from "react-icons/pi";

// const AppointmentDetail = ({ data }) => {
//   const petParent = [data.booker?.first_name, data.booker?.last_name].filter(Boolean).join(" ") || "NA";
//   const pet = `${data.pet?.name || "NA"} (${data.pet?.breed || "NA"})`;
//   const veterinarian = [data.provider?.first_name, data.provider?.last_name].filter(Boolean).join(" ") || "NA";
//   const datetime = formatDateTime(data.scheduled_at);
//   const date = datetime.split(" ")[0] || "";
//   const time = datetime.split(" ").slice(1).join(" ") || "";
//   const image = data.pet?.profile_image_url || "https://placehold.co/80x80";
//   const serviceProvider = data.service_provider?.name || "NA";
//   const status = data.status || "NA";

//   // All info rows together
//   const infoItems = [
//     { icon: FaRegUser, label: petParent },
//     { icon: FaPaw, label: pet },
//     { icon: FaStethoscope, label: veterinarian },
//     { icon: FaRegCalendarAlt, label: date },
//     { icon: FaRegClock, label: time },
//     { icon: PiNotepadBold, label: serviceProvider },
//   ];

//   // Split into two columns dynamically (left/right)
//   const mid = Math.ceil(infoItems.length / 2);
//   const leftInfo = infoItems.slice(0, mid);
//   const rightInfo = infoItems.slice(mid);

//   return (
//     <div className="flex flex-col gap-8 mt-6 lg:mt-10">
//       {/* Header with Avatar + Status */}
//       <div className="flex flex-col sm:flex-row sm:items-center sm:justify-items-start gap-4 lg:gap-40">
//         <AvatarBlock image={image} name={pet} />
//         <StatusBadge status={status} />
//       </div>

//       {/* Info Section in Two Columns */}
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-8 text-gray-800 text-sm sm:text-base mt-2 pl-4 lg:ml-20">
//         {/* Left Column */}
//         <div className="flex flex-col gap-3">
//           {leftInfo.map((item, idx) => (
//             <InfoRow key={idx} icon={item.icon}>
//               {item.label}
//             </InfoRow>
//           ))}
//         </div>

//         {/* Right Column */}
//         <div className="flex flex-col gap-3">
//           {rightInfo.map((item, idx) => (
//             <InfoRow key={idx} icon={item.icon}>
//               {item.label}
//             </InfoRow>
//           ))}
//         </div>
//       </div>

//       {/* Notes Section */}
//       {data.notes && (
//         <div>
//           <SectionHeader className="mt-2 lg:mt-6">Notes</SectionHeader>
//           <div className="mt-4 p-4 bg-gray-50 rounded-lg">
//             <p className="text-gray-700">{data.notes}</p>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default AppointmentDetail;


/**
 * Appointment Detail Component
 * - Displays comprehensive appointment information
 * - Shows pet, owner, veterinarian, and scheduling details
 * - Includes appointment status and notes
 * - Responsive two-column layout for details
 */

import React, { useState } from "react";
import { FaRegCalendarAlt, FaRegClock, FaRegUser, FaPaw, FaStethoscope, FaPhone, FaEnvelope, FaTag, FaRupeeSign } from "react-icons/fa";
import AvatarBlock from "./Shared/AvatarBlock";
import InfoRow from "./Shared/InfoRow";
import StatusBadge from "./Shared/StatusBadge";
import SectionHeader from "./Shared/SectionHeader";
import ImageModal from "../common/ImageModal";
import { formatDateTime } from "./Shared/helpers";
import { PiNotepadBold } from "react-icons/pi";

const AppointmentDetail = ({ data }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Extract and format data
  const petParent = [data.booker?.first_name, data.booker?.last_name].filter(Boolean).join(" ") || "NA";
  const petName = data.pet?.name || "NA";
  const petSpecies = data.pet?.species || "NA";
  const petBreed = data.pet?.breed || "NA";
  const veterinarian = [data.provider?.first_name, data.provider?.last_name].filter(Boolean).join(" ") || "NA";
  const datetime = formatDateTime(data.scheduled_at);
  const date = datetime.split(" ")[0] || "";
  const time = datetime.split(" ").slice(1).join(" ") || "";
  const image = data.pet?.profile_image_url || "https://placehold.co/80x80";
  const serviceName = data.service_provider?.name || "NA";
  const serviceDescription = data.service_provider?.description || "NA";
  const serviceRate = data.service_provider?.rate || "NA";
  const serviceCurrency = data.service_provider?.currency || "NA";
  const status = data.status || "NA";
  const uuid = data.uuid || "NA";

  // Basic info items
  const basicInfo = [
    { icon: FaPaw, label: `${petName} (${petSpecies}, ${petBreed})` },
    { icon: FaRegCalendarAlt, label: date },
    { icon: FaRegClock, label: time },
  ];



  // Service info items
  const serviceInfo = [
    { icon: PiNotepadBold, label: serviceName },
    { icon: FaTag, label: serviceDescription },
    { icon: FaRupeeSign, label: `${serviceRate} ${serviceCurrency}` },
  ];

  return (
    <div className="flex flex-col gap-8 mt-6 lg:mt-10">
      {/* Header with Avatar + Status + UUID */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <AvatarBlock image={image} name={petName} onImageClick={setSelectedImage} />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <StatusBadge status={status} />
          <div className="text-xs text-gray-500">ID: {uuid}</div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-gray-800 text-sm sm:text-base">
        {basicInfo.map((item, idx) => (
          <InfoRow key={idx} icon={item.icon}>
            {item.label}
          </InfoRow>
        ))}
      </div>

      {/* Contact Information */}
      <div>
        <SectionHeader>Contact Information</SectionHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* Pet Parent Contact */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <InfoRow icon={FaRegUser}>{petParent}</InfoRow>
            <div className="ml-8 mt-2 space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FaPhone className="w-4 h-4" />
                <span>{data.booker?.mobile || "NA"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FaEnvelope className="w-4 h-4" />
                <span>{data.booker?.email || "NA"}</span>
              </div>
            </div>
          </div>

          {/* Provider Contact */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <InfoRow icon={FaStethoscope}>{veterinarian}</InfoRow>
            <div className="ml-8 mt-2 space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FaPhone className="w-4 h-4" />
                <span>{data.provider?.mobile || "NA"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FaEnvelope className="w-4 h-4" />
                <span>{data.provider?.email || "NA"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Information */}
      <div>
        <SectionHeader>Service Details</SectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {serviceInfo.map((item, idx) => (
            <InfoRow key={idx} icon={item.icon}>
              {item.label}
            </InfoRow>
          ))}
        </div>
      </div>

      {/* Notes Section */}
      {data.notes && (
        <div>
          <SectionHeader>Notes</SectionHeader>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">{data.notes}</p>
          </div>
        </div>
      )}

      {/* Image Modal */}
      <ImageModal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageSrc={selectedImage}
        altText={`${petName} profile image`}
      />
    </div>
  );
};

export default AppointmentDetail;
